import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { itineraryApi } from '../services/api';
import { Loader2, Plus, X, Scale, Star, DollarSign, Thermometer, Shield, MapPin, Plane, ChevronRight, Trophy } from 'lucide-react';

const TripComparePage = () => {
  const navigate = useNavigate();
  const [destinations, setDestinations] = useState([
    { city: '', country: '' },
    { city: '', country: '' },
  ]);
  const [nationality, setNationality] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const updateDest = (index, field, value) => {
    setDestinations(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };

  const addDestination = () => {
    if (destinations.length < 4) setDestinations(prev => [...prev, { city: '', country: '' }]);
  };

  const removeDest = (index) => {
    if (destinations.length > 2) setDestinations(prev => prev.filter((_, i) => i !== index));
  };

  const handleCompare = async () => {
    const valid = destinations.filter(d => d.city && d.country);
    if (valid.length < 2) { toast.error('Please fill in at least 2 destinations'); return; }
    setLoading(true);
    try {
      const response = await itineraryApi.compareDestinations({
        destinations: valid,
        nationality: nationality || undefined,
      });
      if (response.data.success) {
        setResult(response.data.comparison);
        toast.success('Comparison ready!');
      } else {
        toast.error(response.data.error || 'Comparison failed');
      }
    } catch (error) {
      toast.error('Failed to compare destinations');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'from-emerald-400 to-emerald-600';
    if (score >= 6) return 'from-amber-400 to-amber-600';
    return 'from-red-400 to-red-600';
  };

  const highestScore = result?.destinations ? Math.max(...result.destinations.map(d => d.overall_score || 0)) : 0;

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="trip-compare-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2d1b4e] via-[#1a3a5f] to-[#0B3C5D]" />
        <div className="absolute top-24 left-20 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-32 right-16 w-80 h-80 bg-teal-400/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2.5s'}} />
      </div>
      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ChevronRight className="w-4 h-4 rotate-180" /> Back to Dashboard
            </button>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Scale className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Trip Comparison</h1>
            <p className="text-white/70">Compare destinations side-by-side to find your perfect trip</p>
          </motion.div>

          {/* Input Form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-8 mb-8">
            <div className="grid gap-4 mb-6">
              {destinations.map((dest, idx) => (
                <div key={`dest-${idx}`} className="flex items-end gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-ocean to-ocean-light rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-ocean/70 text-xs">City</Label>
                      <Input value={dest.city} onChange={(e) => updateDest(idx, 'city', e.target.value)}
                        placeholder="e.g., Paris" className="h-11 border-gray-200" data-testid={`compare-city-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-ocean/70 text-xs">Country</Label>
                      <Input value={dest.country} onChange={(e) => updateDest(idx, 'country', e.target.value)}
                        placeholder="e.g., France" className="h-11 border-gray-200" data-testid={`compare-country-${idx}`} />
                    </div>
                  </div>
                  {destinations.length > 2 && (
                    <Button variant="ghost" size="icon" onClick={() => removeDest(idx)}
                      className="text-gray-400 hover:text-red-500 flex-shrink-0" data-testid={`remove-dest-${idx}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-start">
              {destinations.length < 4 && (
                <Button variant="outline" onClick={addDestination} className="border-dashed border-teal-300 text-teal-700" data-testid="add-destination-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Destination
                </Button>
              )}
              <div className="flex-1">
                <Input value={nationality} onChange={(e) => setNationality(e.target.value)}
                  placeholder="Your nationality (optional, for visa info)" className="h-11 border-gray-200" data-testid="compare-nationality" />
              </div>
            </div>

            <Button className="mt-6 w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-semibold hover:from-amber-600 hover:to-orange-600"
              onClick={handleCompare} disabled={loading} data-testid="compare-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Scale className="w-5 h-5 mr-2" />}
              {loading ? 'Comparing...' : 'Compare Destinations'}
            </Button>
          </motion.div>

          {/* Results */}
          <AnimatePresence>
            {result && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                {/* Summary */}
                {result.comparison_summary && (
                  <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white rounded-2xl p-6 mb-8">
                    <h3 className="font-serif text-xl mb-2">Comparison Summary</h3>
                    <p className="text-teal-100">{result.comparison_summary}</p>
                  </div>
                )}

                {/* Cards Grid */}
                <div className={`grid gap-6 ${result.destinations?.length === 2 ? 'md:grid-cols-2' : result.destinations?.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
                  {result.destinations?.map((dest, idx) => {
                    const isWinner = dest.overall_score === highestScore;
                    return (
                      <motion.div key={`compare-item-${idx}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className={`bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border-2 p-6 relative overflow-hidden ${isWinner ? 'border-amber-400' : 'border-white/30'}`}
                        data-testid={`compare-result-${idx}`}>
                        {isWinner && (
                          <div className="absolute top-3 right-3">
                            <div className="bg-amber-400 text-teal-900 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                              <Trophy className="w-3 h-3" /> Top Pick
                            </div>
                          </div>
                        )}
                        <div className="mb-4">
                          <h3 className="font-serif text-2xl text-gray-900">{dest.city}</h3>
                          <p className="text-gray-500 text-sm flex items-center gap-1"><MapPin className="w-3 h-3" />{dest.country}</p>
                        </div>

                        {/* Score */}
                        <div className="mb-5">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Overall Score</span>
                            <span className="text-lg font-bold text-gray-900">{dest.overall_score}/10</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-3">
                            <div className={`bg-gradient-to-r ${getScoreColor(dest.overall_score)} h-3 rounded-full transition-all`}
                              style={{ width: `${(dest.overall_score || 0) * 10}%` }} />
                          </div>
                        </div>

                        {/* Key metrics */}
                        <div className="space-y-3 mb-5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-500" />Daily Cost</span>
                            <span className="font-medium">${dest.estimated_daily_cost_usd}/day</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 flex items-center gap-2"><Thermometer className="w-4 h-4 text-orange-500" />Avg Temp</span>
                            <span className="font-medium">{dest.avg_temp_c}°C</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 flex items-center gap-2"><Shield className="w-4 h-4 text-blue-500" />Safety</span>
                            <span className="font-medium">{dest.safety_rating}/10</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 flex items-center gap-2"><Plane className="w-4 h-4 text-teal-500" />Flight</span>
                            <span className="font-medium text-xs">{dest.flight_cost_estimate}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Visa</span>
                            <span className={`font-medium ${dest.visa_required ? 'text-red-500' : 'text-green-500'}`}>
                              {dest.visa_required ? 'Required' : 'Not Required'}
                            </span>
                          </div>
                        </div>

                        {/* Best for tags */}
                        {dest.best_for && (
                          <div className="mb-4">
                            <p className="text-xs text-gray-500 mb-2">Best for</p>
                            <div className="flex flex-wrap gap-1">
                              {dest.best_for.map((tag, i) => (
                                <span key={`tag-${tag}`} className="bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full text-xs">{tag}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Highlights */}
                        {dest.highlights && (
                          <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-2">Highlights</p>
                            <ul className="space-y-1">
                              {dest.highlights.map((h, i) => (
                                <li key={`hl-${i}`} className="text-xs text-gray-700 flex items-start gap-1">
                                  <ChevronRight className="w-3 h-3 text-teal-500 mt-0.5 flex-shrink-0" />{h}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {dest.weather_summary && (
                          <p className="text-xs text-gray-400 mt-3 italic">{dest.weather_summary}</p>
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                {/* Recommendation */}
                {result.recommendation && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                    className="mt-8 bg-white/95 backdrop-blur-xl border border-amber-200/50 rounded-2xl p-6 shadow-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <Star className="w-6 h-6 text-amber-500" />
                      <h3 className="font-serif text-xl text-gray-900">Our Recommendation</h3>
                    </div>
                    <p className="text-gray-700">{result.recommendation}</p>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-center mt-8">
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="text-white border-white/30 hover:bg-white/10 rounded-full px-6" data-testid="back-to-dashboard-bottom">
              <ChevronRight className="w-4 h-4 rotate-180 mr-2" /> Back to Dashboard
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TripComparePage;
